
const PROBE_ATTEMPTS = 3;
const PROBE_TIMEOUT_MS = 1200;
const ACTION_ACK_MS = 700;

class DivanoCapabilityGate extends HTMLElement {
  #cleanups = new Set();
  #ready = false;
  #degraded = false;

  connectedCallback() {
    this.capability = this.getAttribute('capability') || 'chat';

    if (window.Shopify && window.Shopify.designMode) {
      this.#reveal('design-mode');
      return;
    }

    const onReady = () => { this.#degraded = false; this.#probe(0); };
    window.addEventListener(`divano:${this.capability}:ready`, onReady);
    this.#cleanups.add(() => window.removeEventListener(`divano:${this.capability}:ready`, onReady));

    this.#probe(0);
    this.#watchActions();
  }

  disconnectedCallback() {
    for (const fn of this.#cleanups) fn();
    this.#cleanups.clear();
  }

  #probe(attempt) {
    if (this.#ready || this.#degraded || !this.isConnected) return;

    const nonce = `${this.capability}-${attempt}-${Math.round(performance.now())}`;
    let answered = false;

    const onPong = (event) => {
      const detail =  (event).detail;
      if (!detail || detail.nonce !== nonce) return;
      answered = true;
      if (this.#degraded || !this.isConnected) return;
      this.#reveal('pong');
    };
    window.addEventListener(`divano:${this.capability}:pong`, onPong);
    const stopProbe = () => window.removeEventListener(`divano:${this.capability}:pong`, onPong);
    this.#cleanups.add(stopProbe);

    try {
      window.dispatchEvent(new CustomEvent(`divano:${this.capability}:ping`, { detail: { nonce } }));
    } catch (_) {
    }

    setTimeout(() => {
      stopProbe();
      this.#cleanups.delete(stopProbe);
      if (!this.isConnected) return;
      if (!answered && !this.#ready && attempt + 1 < PROBE_ATTEMPTS) this.#probe(attempt + 1);
    }, PROBE_TIMEOUT_MS * (attempt + 1));
  }

  #watchActions() {
    const onAction = () => {
      if (!this.#ready) return;
      let acked = false;
      const onAck = () => { acked = true; };
      window.addEventListener(`divano:${this.capability}:opened`, onAck, { once: true });
      setTimeout(() => {
        window.removeEventListener(`divano:${this.capability}:opened`, onAck);
        if (!this.isConnected) return;
        if (!acked) {
          this.#degraded = true;
          this.#hide('action-unacknowledged');
        }
      }, ACTION_ACK_MS);
    };
    window.addEventListener(`divano:${this.capability}:open`, onAction);
    this.#cleanups.add(() => window.removeEventListener(`divano:${this.capability}:open`, onAction));
  }

  #reveal(reason) {
    if (this.#ready || !this.isConnected) return;
    this.#ready = true;
    this.dataset.ready = reason;
    this.removeAttribute('hidden');
  }

  #hide(reason) {
    this.#ready = false;
    delete this.dataset.ready;
    this.dataset.hiddenReason = reason;
    this.setAttribute('hidden', '');
  }
}

if (!document.getElementById('divano-capability-gate-hidden-guard')) {
  const style = document.createElement('style');
  style.id = 'divano-capability-gate-hidden-guard';
  style.textContent = 'divano-capability-gate[hidden]{display:none !important}';
  document.head.appendChild(style);
}

if (!customElements.get('divano-capability-gate')) {
  customElements.define('divano-capability-gate', DivanoCapabilityGate);
}
